import React, { useCallback, useState } from 'react';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { centsToDollarsDecimal, dollarsToCents, removeNonDigit } from '@common/paymentsUtils/currencyUtils';
import { MOVE_IN_COSTS_NOTE_MAX_LENGTH } from '@rental-manager/constants/PaymentConstants';
import { isMoveInCostsNoteLengthValid, validateSecurityDepositInputs } from '../validation';
import errorMessages from '@common/constants/errorMessages';
import { MOVE_IN_COSTS_PAYMENT_LIMIT_ERROR } from '@rental-manager/constants/NotificationConstants';
import userPersonas from '@common/constants/userPersonas';
import {
  AdornedInput,
  Box,
  Button,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Paragraph,
  TextButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@zillow/constellation-10';
import Modal from '@common/components/ConstellationV10Modal';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  saveStateToReduxStore: (e) => Promise<any>;
  securityDepositMandatesFromLease: Array<Record<string, string>>;
  editMode: boolean;
  fromDashboard: boolean;
  securityDepositPaymentAmount: number;
  securityDepositNotes: string;
  securityDepositStartsOpen: boolean;
  moveInCostsPaymentLimits: number;
  totalPaymentAmount: number;
  showModalNotification: (args: string) => Record<string, any>;
  individualLimit: number;
  securityDeposit: number;
  pageVisited: Set<number>;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  onClose: () => void;
  currentPage: number;
  setupNewTerm: boolean;
  togglePrevPage: () => void;
}

const SecurityDepositPage: React.FC<Props> = ({
  saveStateToReduxStore,
  securityDepositMandatesFromLease,
  editMode = false,
  fromDashboard = false,
  securityDepositPaymentAmount,
  securityDepositNotes,
  securityDepositStartsOpen,
  moveInCostsPaymentLimits,
  totalPaymentAmount: propsTotalPaymentAmount,
  showModalNotification,
  securityDeposit,
  individualLimit,
  pageVisited,
  currentProperty,
  user,
  onClose,
  currentPage,
  setupNewTerm,
  togglePrevPage,
}) => {
  const initialIsOpen =
    !editMode && !pageVisited?.has(1) && securityDeposit && securityDepositPaymentAmount === 0
      ? true
      : securityDepositStartsOpen;

  const initialPaymentAmount =
    !editMode && !pageVisited?.has(1) && securityDeposit && securityDepositPaymentAmount === 0
      ? String(centsToDollarsDecimal(securityDeposit))
      : securityDepositPaymentAmount === 0
        ? ''
        : String(centsToDollarsDecimal(securityDepositPaymentAmount));

  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const [totalPaymentAmount, setTotalPaymentAmount] = useState(
    securityDepositPaymentAmount === 0
      ? propsTotalPaymentAmount
      : propsTotalPaymentAmount - securityDepositPaymentAmount,
  );
  const [moveInCosts, setMoveInCosts] = useState({
    moveInCostsPaymentType: 'Security Deposit',
    moveInCostsNotes: securityDepositNotes,
    moveInCostsPaymentAmount: initialPaymentAmount,
  });
  const [errors, setErrors] = useState({
    moveInCostsPaymentType: '',
    moveInCostsNotes: '',
    moveInCostsPaymentAmount: '',
  });

  const toggleOpen = useCallback((): void => {
    setIsOpen(true);
  }, []);

  const skipSecurityDepositPage = useCallback(async (): Promise<any> => {
    setIsOpen(false);

    IreneOnboardingCSEvents.emitSkipSecurityDepositEvent({
      currentProperty,
      user,
    });

    const payload = {
      securityDepositSettings: {
        securityDepositPaymentAmount: 0,
        securityDepositNotes: '',
        securityDepositStartsOpen: false,
      },
    };

    await saveStateToReduxStore(payload);
  }, [currentProperty, user, saveStateToReduxStore]);

  const handleNotesChange = useCallback((e): void => {
    const key = e.target.dataset.statekey;
    let value = e.target.value;
    let error = '';

    if (!isMoveInCostsNoteLengthValid(value)) {
      value = value.substring(0, MOVE_IN_COSTS_NOTE_MAX_LENGTH);
      error = errorMessages.MOVE_IN_COSTS_NOTE_MAX_LENGTH;
    }

    setMoveInCosts((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: error }));
  }, []);

  const handleAmountChange = useCallback((e): void => {
    e.preventDefault();
    const key = e.target.dataset.statekey;
    const value = e.target.value;
    const numericValue = removeNonDigit(value);

    setMoveInCosts((prev) => ({ ...prev, moveInCostsPaymentAmount: numericValue }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  }, []);

  const handleSaveStateToReduxStore = useCallback(
    async (e): Promise<any> => {
      e.preventDefault();

      const validInputs = validateSecurityDepositInputs({
        moveInCosts,
        individualLimit,
        handleErrors: (errors) => {
          setErrors(errors);
        },
      });

      if (!validInputs) {
        return;
      }

      const moveInCostsPaymentAmount = parseInt(dollarsToCents(moveInCosts.moveInCostsPaymentAmount), 10);

      if (totalPaymentAmount + moveInCostsPaymentAmount >= moveInCostsPaymentLimits) {
        await showModalNotification(MOVE_IN_COSTS_PAYMENT_LIMIT_ERROR);
        return;
      }

      IreneOnboardingCSEvents.emitContinueFromSecurityDepositEvent({
        currentProperty,
        user,
      });

      const payload = {
        securityDepositSettings: {
          securityDepositPaymentAmount: parseInt(dollarsToCents(moveInCosts.moveInCostsPaymentAmount), 10),
          securityDepositNotes: moveInCosts.moveInCostsNotes,
          securityDepositStartsOpen: isOpen,
        },
      };

      await saveStateToReduxStore(payload);
    },
    [
      moveInCosts,
      errors,
      totalPaymentAmount,
      isOpen,
      moveInCostsPaymentLimits,
      showModalNotification,
      currentProperty,
      user,
      saveStateToReduxStore,
    ],
  );

  const getSecurityMandatesFromLease = useCallback(() => {
    return securityDepositMandatesFromLease.map(({ title, text }) => {
      return {
        question: title,
        answer: text.split('\n').map((innerText) => <p>{innerText}</p>),
      };
    });
  }, [securityDepositMandatesFromLease]);

  const renderOpenedSection = (): JSX.Element => {
    const notesHelp = `${moveInCosts.moveInCostsNotes.length}/${MOVE_IN_COSTS_NOTE_MAX_LENGTH}`;
    return (
      <>
        <FormField
          control={
            <AdornedInput
              fluid
              error={Boolean(errors.moveInCostsPaymentAmount)}
              input={
                <AdornedInput.Input
                  type="number"
                  placeholder="Enter amount"
                  className="fs-exclude input-caret-hide"
                  value={!moveInCosts.moveInCostsPaymentAmount ? '' : moveInCosts.moveInCostsPaymentAmount}
                  onChange={handleAmountChange}
                  onBlur={handleAmountChange}
                  onFocus={handleAmountChange}
                  data-statekey="moveInCostsPaymentAmount"
                />
              }
              size="md"
              startAdornment={<AdornedInput.Adornment id="start-adornment">$</AdornedInput.Adornment>}
            />
          }
          description={errors.moveInCostsPaymentAmount && <FormHelp error>{errors.moveInCostsPaymentAmount}</FormHelp>}
          label={<Label>Amount</Label>}
        />

        <FormField
          control={
            <Input
              data-statekey="moveInCostsNotes"
              className="fs-exclude"
              placeholder="Enter any additional info"
              value={moveInCosts.moveInCostsNotes}
              onChange={handleNotesChange}
              onBlur={handleNotesChange}
            />
          }
          description={
            errors.moveInCostsNotes ? (
              <FormHelp error>{errors.moveInCostsNotes}</FormHelp>
            ) : (
              <FormHelp>{notesHelp}</FormHelp>
            )
          }
          label={<Label>Note (optional)</Label>}
        />
      </>
    );
  };

  const renderMainSection = (): JSX.Element => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'token(spacing.sm) !important',
        }}
      >
        <Paragraph textStyle="body-lg-bold">Would you like to collect a security deposit?</Paragraph>
        <ToggleButtonGroup
          aria-label="security deposit options"
          conjoined
          value={isOpen ? 'Yes' : ''}
          role="group"
          size="md"
        >
          <React.Fragment key=".0">
            <ToggleButton css={{ width: '100%' }} value="Yes" onClick={toggleOpen}>
              Yes
            </ToggleButton>
            <ToggleButton css={{ width: '100%' }} value="No" onClick={skipSecurityDepositPage}>
              No
            </ToggleButton>
          </React.Fragment>
        </ToggleButtonGroup>
        {isOpen && renderOpenedSection()}
      </Box>
    );
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={onClose}
      header={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <TextButton
            textStyle="body-sm-bold"
            tone="brand"
            css={{ float: 'left' }}
            onClick={currentPage === 1 && setupNewTerm ? undefined : togglePrevPage}
          >
            Back
          </TextButton>
          <Heading level={3} textStyle="heading-xs" css={{ display: 'flex', justifyContent: 'center' }}>
            {fromDashboard ? 'Move-in costs' : 'Payment setup'}
          </Heading>
        </Box>
      }
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'token(spacing.sm) !important',
            padding: 'token(spacing.lg) !important',
          }}
        >
          {renderMainSection()}
          <FaqComponent
            faqs={[
              ...getSecurityMandatesFromLease(),
              ...FaqConstants.ONBOARDING_V2.MOVE_IN_COSTS.SECURITY_DEPOSIT_PAGE,
            ]}
          />
        </Box>
      }
      footer={
        isOpen && (
          <Box css={{ padding: 'token(spacing.sm) !important' }}>
            <Button emphasis="filled" onClick={handleSaveStateToReduxStore}>
              Continue
            </Button>
          </Box>
        )
      }
    />
  );
};

export default SecurityDepositPage;
